import { Component, DoCheck, ElementRef, EventEmitter, HostListener, Input, OnDestroy, OnInit, Output, ViewChild } from '@angular/core';
import { UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { MatSnackBar } from '@angular/material/snack-bar';
import { TranslateService } from '@ngx-translate/core';
import { Subject } from 'rxjs';
import { catchError, debounceTime, filter, map, switchMap, takeUntil } from 'rxjs/operators';
import { of } from 'rxjs';
import { CountryService } from 'src/app/_services/country.service';
import { SnackbarService } from 'src/app/_services/snackBar.service';
import { SnackBarErrorComponent } from 'src/app/components/snack-bar-error/snack-bar-error.component';
import { DataService } from 'src/app/dashboard/_services/data.service';
import { StudentOption } from 'src/app/user-management/_model/student-option.model';
import { ParentService } from 'src/app/user-management/_services/parent.service';
import { StudentLookupService } from 'src/app/user-management/_services/student-lookup.service';
import { ModalConfig } from '../modal-config';
import { ModalService } from '../modal.service';
import { BusinessConflict, ConflictSource, Resolutions, CONFLICT_ANCHOR } from '../conflict/conflict.model';

/**
 * What the modal shell needs to draw this flow's header buttons: which step is
 * showing, and whether the button for it can be pressed.
 */
export interface AddParentHeaderState {
  step: number;
  canContinue: boolean;
  saveBlocked: boolean;
  saving: boolean;
}

/**
 * Add Parent: pick the student, describe the guardian, post once.
 *
 * Deliberately separate from the student modal even though both talk to the
 * same business-validation contract — this one creates a guardian for a
 * student that already exists, and the tested add-student flow must not move
 * to accommodate it. What they genuinely share (the conflict resolver, the
 * map, the parent field set) is shared as components, not as one component
 * doing two jobs.
 */
@Component({
  selector: 'app-add-parent-modal',
  templateUrl: './add-parent-modal.component.html',
  styleUrls: ['./add-parent-modal.component.scss']
})
export class AddParentModalComponent implements OnInit, DoCheck, OnDestroy {
  @Input() config!: ModalConfig;
  @Input() lang: string;
  /**
   * Back / Continue / Save live in the modal's header, which belongs to the
   * shell. It cannot read this component's state directly — a view query
   * resolves after the header has already been checked — so the state is
   * pushed to it instead.
   */
  @Output() headerState = new EventEmitter<AddParentHeaderState>();
  // this modal's own file input — never look it up by id, other components on
  // the same page use the same id and getElementById would hand us theirs
  @ViewChild('fileUploadInput') fileUploadInput: ElementRef<HTMLInputElement>;
  /** The search box and its dropdown, so a click outside can close it. */
  @ViewChild('studentCombo') comboRef: ElementRef<HTMLElement>;

  /** 1 = which student, 2 = who the parent is. */
  step = 1;

  /* ------------------------------ step 1 ------------------------------ */

  /**
   * The endpoint has no minimum term length — a single character searches, and
   * the server's cap of 5 is what keeps that cheap. Only an empty box is not a
   * search.
   */
  readonly minSearchLength = 1;
  searchTerm = '';
  results: StudentOption[] = [];
  searching = false;
  /** True once a search has actually run, so the empty state waits its turn. */
  searched = false;
  searchFailed = false;
  resultsOpen = false;
  selectedStudent: StudentOption = null;
  /** A 422 on student_id — the picked student is not one this admin can use. */
  studentServerError: string = null;
  private terms$ = new Subject<string>();

  /* ------------------------------ step 2 ------------------------------ */

  addParentForm: UntypedFormGroup;
  countries = [];
  phoneServerErrors: string[] = [];

  latitude: number = null;
  longitude: number = null;
  address: string;
  mapControls;
  mapView = '';

  imageFile;
  image;

  /**
   * The one conflict the endpoint is currently reporting. It reports one at a
   * time and so does the UI: when the next one arrives this is replaced, so a
   * second conflict on the same field cannot collide with a stale panel.
   */
  conflict: BusinessConflict = null;
  /** Answers gathered so far — sent as resolutions[field][source] on every save. */
  resolutions: Resolutions = {};
  savingParent = false;

  showedNumber: number;
  private destroy$ = new Subject<void>();
  private lastHeaderState: AddParentHeaderState = null;

  /**
   * Terminal verdicts are about the guardian the payload resolves to and the
   * student they would be filed on. Touch either and the verdict is stale.
   */
  private readonly terminalFields = ['student_parent_limit', 'parent_already_linked'];

  constructor(
    private _snackBar: MatSnackBar,
    private snackbarService: SnackbarService,
    private countryService: CountryService,
    private translate: TranslateService,
    private dataService: DataService,
    private parentService: ParentService,
    private studentLookup: StudentLookupService,
    private modalService: ModalService
  ) { }

  ngOnInit(): void {
    this.mapView = 'add-parent';
    this.initFormFields();
    this.initMapControls();
    this.countries = this.countryService.countries;
    this.wireSearch();
    this.dataService.getRowsNumberShown().pipe(takeUntil(this.destroy$)).subscribe(res => {
      this.showedNumber = res;
    });
    // opened from a student row: the student is already known, so the picker
    // has nothing left to ask
    const student = this.config?.data?.student;
    if (student?.id) {
      this.selectStudent(this.toOption(student));
      this.step = 2;
    }
  }

  /**
   * Anything that moves the flow along moves a header button with it, so the
   * state is derived once per check rather than re-emitted by hand from every
   * place that could change it. Emits only on a real change, and the shell
   * applies it outside the current pass — so this settles after one extra
   * cycle instead of looping.
   */
  ngDoCheck(): void {
    const next: AddParentHeaderState = {
      step: this.step,
      canContinue: !!this.selectedStudent,
      saveBlocked: this.saveBlocked,
      saving: this.savingParent,
    };
    if (!this.lastHeaderState
      || Object.keys(next).some(key => next[key] !== this.lastHeaderState[key])) {
      this.lastHeaderState = next;
      this.headerState.emit(next);
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private initFormFields(): void {
    // the same field set and rules the Edit Parent modal validates against
    this.addParentForm = new UntypedFormGroup({
      name: new UntypedFormControl('', [Validators.required]),
      national_id: new UntypedFormControl('', [Validators.required, Validators.pattern('[a-zA-Z0-9]*')]),
      gender: new UntypedFormControl(''),
      country_code: new UntypedFormControl('', Validators.required),
      phone: new UntypedFormControl('', [Validators.required]),
      email: new UntypedFormControl('', [Validators.email, Validators.pattern('^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\\.[a-zA-Z]{2,4}$')])
    });
  }

  /* =========================== step 1: the student =========================== */

  /**
   * One search per pause, not one per keystroke. `input` covers pasting too,
   * so a pasted ID waits out the same window as a typed one instead of firing
   * mid-paste.
   */
  private wireSearch(): void {
    this.terms$.pipe(
      map(term => (term || '').trim()),
      debounceTime(350),
      filter(term => term.length >= this.minSearchLength),
      // switchMap, so a term abandoned mid-flight can never land on top of the
      // one that replaced it
      switchMap(term => this.studentLookup.search(term).pipe(
        map(matches => ({ term, matches, failed: false })),
        catchError(() => of({ term, matches: [] as StudentOption[], failed: true }))
      )),
      takeUntil(this.destroy$)
    ).subscribe(result => {
      // deleting back below the minimum stops emitting rather than cancelling,
      // so a reply can still arrive for a term the box no longer holds
      if ((this.searchTerm || '').trim() !== result.term) {
        return;
      }
      this.searching = false;
      this.searchFailed = result.failed;
      this.results = result.matches;
      this.searched = true;
      this.resultsOpen = true;
    });
  }

  /**
   * The state the dropdown shows is decided here, on the keystroke, and the
   * request itself waits out the debounce. `input` fires on paste too, so a
   * pasted ID takes exactly the same path as a typed one.
   */
  onSearchInput(term: string): void {
    this.searchTerm = term;
    this.resultsOpen = true;
    this.studentServerError = null;
    this.searchFailed = false;
    if ((term || '').trim().length < this.minSearchLength) {
      // too short to mean anything: stop asking, and stop showing whatever the
      // last full term found
      this.results = [];
      this.searched = false;
      this.searching = false;
    } else {
      this.searching = true;
    }
    this.terms$.next(term);
  }

  /** Re-runs the current term after a failed search. */
  retrySearch(): void {
    const term = (this.searchTerm || '').trim();
    this.searchFailed = false;
    if (term.length < this.minSearchLength) {
      return;
    }
    this.searching = true;
    this.studentLookup.search(term).pipe(takeUntil(this.destroy$)).subscribe(
      matches => {
        this.results = matches;
        this.searching = false;
        this.searched = true;
        this.resultsOpen = true;
      },
      () => {
        this.searching = false;
        this.searchFailed = true;
      }
    );
  }

  get canSearch(): boolean {
    return (this.searchTerm || '').trim().length >= this.minSearchLength;
  }

  get showsEmptyState(): boolean {
    return this.resultsOpen && this.searched && !this.searching && !this.searchFailed && !this.results.length;
  }

  selectStudent(student: StudentOption): void {
    // a different student is a different question: every answer given about
    // the old one, and every verdict the server reached, is void
    const changed = this.selectedStudent?.id !== student?.id;
    this.selectedStudent = student;
    this.resultsOpen = false;
    this.results = [];
    this.searched = false;
    this.searchTerm = '';
    this.studentServerError = null;
    if (changed) {
      this.conflict = null;
      this.resolutions = {};
    }
  }

  clearSelectedStudent(): void {
    this.selectedStudent = null;
    this.conflict = null;
    this.resolutions = {};
    this.results = [];
    this.searched = false;
    this.searchTerm = '';
    this.resultsOpen = false;
  }

  goToParentStep(): void {
    if (!this.selectedStudent) {
      return;
    }
    // the map is created with this step, so hand it wherever the address
    // already stands rather than wherever it stood the first time through
    this.initMapControls();
    this.step = 2;
  }

  backToStudentStep(): void {
    this.step = 1;
    this.resultsOpen = false;
  }

  /** Clicking anywhere else in the modal puts the dropdown away. */
  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent): void {
    if (!this.resultsOpen) {
      return;
    }
    const combo = this.comboRef?.nativeElement;
    if (combo && !combo.contains(event.target as Node)) {
      this.resultsOpen = false;
    }
  }

  @HostListener('document:keydown.escape')
  onEscape(): void {
    this.resultsOpen = false;
  }

  /** A student row from the listing table, in the shape the picker speaks. */
  private toOption(student: any): StudentOption {
    return {
      id: student?.id,
      name: student?.name,
      national_id: student?.national_id,
      grade: student?.grade ?? student?.class?.grade?.name,
      class: student?.class?.name ?? student?.class
    };
  }

  studentMeta(student: StudentOption): string {
    return [student?.grade, student?.class].filter(part => !!part).join(' • ');
  }

  trackById(index: number, student: StudentOption): number {
    return student?.id ?? index;
  }

  /* ============================ step 2: the parent ============================ */

  getValidationError(control: string): string {
    let errorMSG;
    if (this.addParentForm.controls[control].errors) {
      switch (control) {
        case 'name':
          errorMSG = this.addParentForm.controls[control].errors.required ? this.translate.instant('edit-parent.name-required') :
            this.addParentForm.controls[control].errors.repeat ? this.translate.instant('edit-parent.name-exists')
              : this.translate.instant('edit-parent.name-valid');
          break;
        case 'national_id':
          errorMSG = this.addParentForm.controls[control].errors.required ? this.translate.instant('edit-parent.id-required') :
            this.addParentForm.controls[control].errors.repeat ? this.translate.instant('edit-parent.id-exists')
              : this.translate.instant('edit-parent.id-valid');
          break;
        case 'phone':
          errorMSG = this.addParentForm.controls[control].errors.required ? this.translate.instant('edit-parent.phone-required') :
            this.addParentForm.controls[control].errors.repeat ? this.translate.instant('edit-parent.phone-exists')
              : this.translate.instant('edit-parent.phone-valid');
          break;
        case 'email':
          errorMSG = this.addParentForm.controls[control].errors.required ? this.translate.instant('edit-parent.email-required') :
            this.addParentForm.controls[control].errors.repeat ? this.translate.instant('edit-parent.email-exists')
              : this.translate.instant('edit-parent.email-valid');
          break;
        default:
          break;
      }
    }
    return errorMSG;
  }

  changeParentProfile(): void {
    const fileUpload = this.fileUploadInput?.nativeElement;
    if (!fileUpload) {
      return;
    }
    // clearing the value first so re-picking the same file still fires `change`
    fileUpload.value = null;
    fileUpload.click();
  }

  onFileSelected(file: FileList): void {
    const picked = file?.item(0);
    if (!picked) {
      return;
    }
    // the upload payload and the preview are set together, so they can never diverge
    this.imageFile = picked;
    const reader = new FileReader();
    reader.onload = (event: any) => {
      this.image = event.target.result;
    };
    reader.readAsDataURL(picked);
  }

  initMapControls(): void {
    this.mapControls = {
      mapPoint: {
        lat: this.latitude,
        long: this.longitude,
      }
    };
  }

  mapEventHandle(event): void {
    switch (event.type) {
      case 'updateAdress':
        this.address = event.data;
        this.latitude = event.lat;
        this.longitude = event.lng;
        break;
    }
  }

  // same effect the map's own clear button has
  clearAddress(): void {
    this.address = '';
    this.latitude = null;
    this.longitude = null;
  }

  /* ============================== conflicts ============================== */

  /** Is the current conflict anchored under this control? */
  showsConflictAt(control: string): boolean {
    return !!this.conflict && CONFLICT_ANCHOR[this.conflict.field] === control;
  }

  /** An unanswered conflict blocks the save; a terminal one blocks it for good. */
  get conflictBlocksSave(): boolean {
    if (!this.conflict) {
      return false;
    }
    return !this.conflict.resolvable || !this.resolutions[this.conflict.field];
  }

  /**
   * Record the choice and show the winning value in the form, so the admin can
   * see what will be saved. Nothing is sent yet — they press Save themselves,
   * which keeps a mis-click undoable through the panel's Change button.
   */
  onConflictChoice(choice: { field: string; source: ConflictSource }): void {
    this.resolutions = { ...this.resolutions, [choice.field]: { source: choice.source } };
    this.applyChoiceToForm(this.conflict, choice.source);
  }

  /**
   * A resolution answers a question about one specific value. The moment the
   * admin edits that value the answer is stale, so drop it — and drop the
   * panel too, since its cards compare values that no longer apply. The next
   * save re-evaluates from scratch.
   */
  onResolvedFieldEdited(field: string): void {
    if (this.resolutions[field]) {
      const next = { ...this.resolutions };
      delete next[field];
      this.resolutions = next;
    }
    if (this.conflict?.field === field) {
      this.conflict = null;
    }
  }

  /**
   * Editing any identity field also clears a terminal verdict: both of them
   * name a specific guardian, and this is no longer that guardian.
   */
  onIdentityEdited(field: string): void {
    this.onResolvedFieldEdited(field);
    if (this.terminalFields.includes(this.conflict?.field)) {
      this.conflict = null;
    }
  }

  /** Put back what the admin originally typed and re-open the choice. */
  onConflictUndo(field: string): void {
    const next = { ...this.resolutions };
    delete next[field];
    this.resolutions = next;
    this.patchParentFromGuardian(this.conflict?.submitted_value);
  }

  /**
   * Adopt the card that was pressed, whole. Each card shows a complete
   * guardian, so "Use this" has to leave the form matching that card exactly —
   * mixing the two sides would show a guardian neither card described.
   *
   * `submitted_value` is the server's echo of what was posted, so it doubles as
   * the "restore what I typed" source when a choice is undone.
   */
  private applyChoiceToForm(conflict: BusinessConflict, source: ConflictSource): void {
    const chosen: any = source === 'existing' ? conflict?.existing_value : conflict?.submitted_value;
    // the terminal cases carry nothing that maps onto a parent field
    if (!chosen || !chosen.parent_national_id) {
      return;
    }
    this.patchParentFromGuardian(chosen);
  }

  /**
   * Shows one whole guardian in the form — name and email included, exactly as
   * its card listed them. Only the keys the card actually carries are written,
   * so a partial payload leaves the rest of what the admin typed alone instead
   * of blanking it.
   */
  private patchParentFromGuardian(values: any): void {
    if (!values) {
      return;
    }
    const phone = this.splitGuardianPhone(values);
    const patch = {
      name: values.parent_name,
      national_id: values.parent_national_id,
      email: values.parent_email,
      country_code: phone.country_code,
      phone: phone.phone,
    };
    for (const control in patch) {
      if (patch[control] !== undefined) {
        this.addParentForm.controls[control].setValue(patch[control] ?? '');
      }
    }
  }

  /**
   * A guardian reports its phone either already split (`parent_country_code` +
   * `parent_phone`) or as the single E.164 string the cards display
   * (`parent_phone_number`), while the form always keeps the two apart. Match
   * the combined form against the known dial codes — longest first, since some
   * are prefixes of others — so the number lands in the right box.
   */
  private splitGuardianPhone(values: any): { country_code?: string; phone?: string } {
    if (values.parent_country_code !== undefined || values.parent_phone !== undefined) {
      return { country_code: values.parent_country_code, phone: values.parent_phone };
    }
    const full: string = values.parent_phone_number;
    if (!full) {
      return { phone: full === undefined ? undefined : '' };
    }
    const code = this.countries
      .map(country => country?.code_plus)
      .filter(code_plus => !!code_plus && full.startsWith(code_plus))
      .sort((a, b) => b.length - a.length)[0];
    return code ? { country_code: code, phone: full.slice(code.length) } : { phone: full };
  }

  /* =============================== saving =============================== */

  get saveBlocked(): boolean {
    return !this.selectedStudent
      || this.addParentForm.invalid
      || this.savingParent
      || this.conflictBlocksSave;
  }

  saveParent(): void {
    if (this.saveBlocked) {
      return;
    }
    this.phoneServerErrors = [];
    const formData = new FormData();
    // the whole point of this endpoint: the guardian and the link, together
    formData.append('student_id', String(this.selectedStudent.id));

    for (const field in this.addParentForm.value) {
      const value = this.addParentForm.value[field];
      // v2 has no null: "no email" means omitting the key, not sending it
      // empty. FormData would also stringify a null to "null".
      if (value === null || value === undefined || value === '' || value === 'noParentGender') {
        continue;
      }
      formData.append(field, value);
    }
    if (this.imageFile) {
      formData.append('image', this.imageFile);
    }
    // address / lat / long are written only when non-empty, so re-adding a
    // guardian without one never wipes the one they already have
    if (this.latitude !== null && this.longitude !== null) {
      formData.append('lat', this.latitude.toString());
      formData.append('long', this.longitude.toString());
      formData.append('address', this.address || '');
    }
    // resolutions[field][source] — the nested shape the endpoint expects.
    // The winning value is never sent; the server resolves it itself.
    for (const field in this.resolutions) {
      formData.append(`resolutions[${field}][source]`, this.resolutions[field].source);
    }

    this.savingParent = true;
    this.parentService.createParentV2(formData).subscribe(
      () => {
        this.savingParent = false;
        this.conflict = null;
        this.updateAllUsersObservable();
        this.closeModalHandler();
      },
      (error) => {
        this.savingParent = false;
        this.handleSaveError(error);
      }
    );
  }

  private handleSaveError(error): void {
    // 409 = a business conflict the admin can answer. Only one is reported per
    // request, so answering it may reveal the next.
    if (error?.status === 409 && error?.error?.conflict) {
      // Replaces the previous one: only the conflict still to be answered is
      // ever on screen. Earlier answers stay in `resolutions`.
      this.conflict = error.error.conflict;
      return;
    }
    const errors = error?.error?.errors;
    if (errors) {
      // student_id can only be wrong about the student, and that question was
      // asked on the first screen — send them back to it with the reason
      if (errors['student_id']) {
        const reason = errors['student_id'][0];
        this.clearSelectedStudent();
        this.studentServerError = reason;
        this.step = 1;
        return;
      }
      if (errors['phone']) {
        this.phoneServerErrors = errors['phone'];
      }
      let matched = false;
      Object.keys(errors).forEach(key => {
        // resolutions.* errors are about the payload we built, not a field the
        // admin can fix; they fall through to the generic message below
        const control = this.addParentForm.controls[key];
        if (control) {
          control.setErrors({ repeat: true });
          control.markAsTouched();
          matched = true;
        }
      });
      if (matched) {
        return;
      }
    }
    this.snackbarService.setSnackBarMessage(error?.error?.message);
    this._snackBar.openFromComponent(SnackBarErrorComponent, {
      duration: 5000,
      horizontalPosition: 'center',
      verticalPosition: 'top',
    });
  }

  private updateAllUsersObservable(): void {
    this.dataService.getUsers$().subscribe(res => {
      this.dataService.setUsers$(res);
    });
  }

  closeModalHandler(): void {
    this.dataService.setRowsNumberShown(this.showedNumber);
    this.modalService.closeModal();
  }
}
